// Date helpers for 'YYYY-MM-DD' strings. Everything is computed in UTC with fixed
// Indonesian names, so the server and the browser always render identical text.

const DAY_NAMES = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu']

const MONTH_NAMES = [
    'Januari',
    'Februari',
    'Maret',
    'April',
    'Mei',
    'Juni',
    'Juli',
    'Agustus',
    'September',
    'Oktober',
    'November',
    'Desember'
]

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des']

// Reference "today" for the sample data, kept fixed so every render is deterministic.
export const DEMO_TODAY = '2026-11-04'

export type DateStyle = 'full' | 'long' | 'medium' | 'short' | 'dayMonth' | 'weekday'

const pad = (value: number): string => String(value).padStart(2, '0')

export const parseDate = (value: string): Date => {
    const [year, month, day] = value.split('-').map(Number)

    return new Date(Date.UTC(year, (month || 1) - 1, day || 1))
}

export const toISODate = (date: Date): string => {
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`
}

export const addDays = (value: string, amount: number): string => {
    const date = parseDate(value)
    date.setUTCDate(date.getUTCDate() + amount)

    return toISODate(date)
}

export const isWeekend = (value: string): boolean => {
    const day = parseDate(value).getUTCDay()

    return day === 0 || day === 6
}

export const schoolDaysBetween = (start: string, end: string): string[] => {
    const days: string[] = []
    let cursor = start

    while (cursor <= end) {
        if (!isWeekend(cursor)) days.push(cursor)
        cursor = addDays(cursor, 1)
    }

    return days
}

export const monthKey = (value: string): string => value.slice(0, 7)

export const formatDate = (value: string, style: DateStyle = 'long'): string => {
    if (!value) return ''

    const date = parseDate(value)
    const day = date.getUTCDate()
    const month = date.getUTCMonth()
    const year = date.getUTCFullYear()

    switch (style) {
        case 'full':
            return `${DAY_NAMES[date.getUTCDay()]}, ${day} ${MONTH_NAMES[month]} ${year}`
        case 'medium':
            return `${day} ${MONTH_SHORT[month]} ${year}`
        case 'short':
            return `${pad(day)}/${pad(month + 1)}/${year}`
        case 'dayMonth':
            return `${day} ${MONTH_SHORT[month]}`
        case 'weekday':
            return DAY_NAMES[date.getUTCDay()]
        default:
            return `${day} ${MONTH_NAMES[month]} ${year}`
    }
}

export const formatMonth = (key: string): string => {
    const [year, month] = key.split('-').map(Number)

    if (!year || !month) return key

    return `${MONTH_NAMES[month - 1]} ${year}`
}

export const shortMonthName = (value: string): string => MONTH_SHORT[parseDate(value).getUTCMonth()]

export const dayNumber = (value: string): string => pad(parseDate(value).getUTCDate())

// Timestamps are stored in UTC and shown in Western Indonesia Time (UTC+7).
const toWIB = (iso: string): Date => new Date(new Date(iso).getTime() + 7 * 60 * 60 * 1000)

export const formatDateTime = (iso: string): string => {
    const date = toWIB(iso)

    return `${date.getUTCDate()} ${MONTH_SHORT[date.getUTCMonth()]} ${date.getUTCFullYear()}, ${pad(date.getUTCHours())}.${pad(date.getUTCMinutes())}`
}

export const formatTimeOnly = (iso: string): string => {
    const date = toWIB(iso)

    return `${pad(date.getUTCHours())}.${pad(date.getUTCMinutes())}`
}

export const dateOfTimestamp = (iso: string): string => toISODate(toWIB(iso))

export const nowInWIB = (): { date: string, time: string, hour: number } => {
    const date = toWIB(new Date().toISOString())

    return {
        date: formatDate(toISODate(date), 'full'),
        time: `${pad(date.getUTCHours())}.${pad(date.getUTCMinutes())}.${pad(date.getUTCSeconds())}`,
        hour: date.getUTCHours()
    }
}

export const greetingForHour = (hour: number): string => {
    if (hour < 11) return 'Selamat pagi'
    if (hour < 15) return 'Selamat siang'
    if (hour < 18) return 'Selamat sore'

    return 'Selamat malam'
}

export const monthsBetween = (start: string, end: string): string[] => {
    const months: string[] = []
    let [year, month] = start.slice(0, 7).split('-').map(Number)
    const [endYear, endMonth] = end.slice(0, 7).split('-').map(Number)

    while (year < endYear || (year === endYear && month <= endMonth)) {
        months.push(`${year}-${pad(month)}`)
        month += 1

        if (month > 12) {
            month = 1
            year += 1
        }
    }

    return months
}
